import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { LOGOS } from '../assets/logos';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiUsers, FiBrain, FiTrendingUp, FiCheck, FiArrowRight } = FiIcons;

const LandingPage = () => {
  const features = [
    {
      icon: FiPlay,
      title: "Entrevistas Asincrónicas",
      description: "Los candidatos responden en su tiempo, sin coordinación de horarios"
    },
    {
      icon: FiBrain,
      title: "Análisis con IA",
      description: "Evaluación automática de respuestas con puntuación inteligente"
    },
    {
      icon: FiUsers,
      title: "Gestión de Candidatos",
      description: "Panel completo para organizar y filtrar prospectos"
    },
    {
      icon: FiTrendingUp,
      title: "Reportes Avanzados",
      description: "Métricas detalladas para optimizar tu proceso de selección"
    }
  ];

  const plans = [
    {
      name: "Gratuito",
      price: "0",
      features: ["5 entrevistas/mes", "IA básica", "Soporte por email"],
      popular: false
    },
    {
      name: "Básico",
      price: "29",
      features: ["25 entrevistas/mes", "IA completa", "Integraciones", "Soporte prioritario"],
      popular: true
    },
    {
      name: "Pro",
      price: "99",
      features: ["100 entrevistas/mes", "Análisis avanzado", "API completa", "Soporte dedicado"],
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-3"
            >
              <img 
                src={LOGOS.BLUE_TRANSPARENT} 
                alt="VIXEA" 
                className="h-10 object-contain" 
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center space-x-4"
            >
              <Link
                to="/login"
                className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                Iniciar Sesión
              </Link>
              <Link
                to="/register"
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Registrarse
              </Link>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-primary to-blue-800">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Revoluciona tus <span className="text-yellow-300">Entrevistas</span>
            </h1>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Plataforma inteligente de entrevistas asincrónicas con análisis automatizado por IA. Optimiza tu proceso de selección en Latinoamérica.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="px-8 py-4 bg-accent text-white rounded-lg font-semibold hover:bg-red-600 transition-colors flex items-center justify-center space-x-2"
              >
                <span>Comenzar Gratis</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </Link>
              <button className="px-8 py-4 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-primary transition-colors">
                Ver Demo
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Rest of the component remains the same */}
      
      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Todo lo que necesitas para reclutar mejor
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Herramientas avanzadas diseñadas específicamente para el mercado latinoamericano
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={feature.icon} className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Planes que se adaptan a tu empresa
            </h2>
            <p className="text-xl text-gray-600">
              Desde startups hasta corporaciones, tenemos el plan perfecto para ti
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-white rounded-xl shadow-lg p-8 ${
                  plan.popular ? 'ring-2 ring-primary scale-105' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-accent text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Más Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                    <span className="text-gray-600 ml-2">/mes</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center space-x-3">
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                    plan.popular
                      ? 'bg-primary text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  Seleccionar Plan
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              ¿Listo para transformar tu proceso de selección?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Únete a las empresas líderes en Latinoamérica que ya confían en VIXEA
            </p>
            <Link
              to="/register"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-accent text-white rounded-lg font-semibold hover:bg-red-600 transition-colors"
            >
              <span>Comenzar Ahora</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-center space-x-3 mb-8">
            <img 
              src={LOGOS.WHITE_TRANSPARENT} 
              alt="VIXEA" 
              className="h-10 object-contain" 
            />
          </div>
          <div className="text-center text-gray-400">
            <p>&copy; 2024 VIXEA. Todos los derechos reservados.</p>
            <p className="mt-2">Revolucionando las entrevistas en Latinoamérica</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;